/**
 * 폴더 서비스
 * 보안 메모용 폴더 시스템 관리
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import { encryptData, decryptData } from './webCryptoService';

const COLLECTION_NAME = 'folders';

/**
 * 기본 시스템 폴더 ID
 */
export const SYSTEM_FOLDERS = {
  ALL: 'all-notes',
  FAVORITES: 'favorites',
  RECENT: 'recent',
  ARCHIVED: 'archived',
  TRASH: 'trash'
};

/**
 * 시스템 폴더 정보
 */
export const SYSTEM_FOLDER_INFO = {
  [SYSTEM_FOLDERS.ALL]: {
    name: '모든 메모',
    icon: 'folder',
    color: '#4A90E2',
    system: true,
    deletable: false
  },
  [SYSTEM_FOLDERS.FAVORITES]: {
    name: '즐겨찾기',
    icon: 'star',
    color: '#FFD700',
    system: true,
    deletable: false
  },
  [SYSTEM_FOLDERS.RECENT]: {
    name: '최근',
    icon: 'clock',
    color: '#8E44AD',
    system: true,
    deletable: false
  },
  [SYSTEM_FOLDERS.ARCHIVED]: {
    name: '보관함',
    icon: 'archive',
    color: '#95A5A6',
    system: true,
    deletable: false
  },
  [SYSTEM_FOLDERS.TRASH]: {
    name: '휴지통',
    icon: 'trash',
    color: '#E74C3C',
    system: true,
    deletable: false
  }
};

/**
 * 폴더 생성
 */
export async function createFolder(userId, folderData) {
  try {
    const { name, color = '#4A90E2', icon = 'folder', parentId = null } = folderData;

    // 폴더 이름 암호화
    const encryptedName = await encryptData(name, userId);

    const folderDoc = {
      userId,
      name: encryptedName,
      color,
      icon,
      parentId, // 상위 폴더 ID (중첩 폴더 지원)
      noteCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), folderDoc);

    return {
      id: docRef.id,
      name,
      color,
      icon,
      parentId,
      noteCount: 0,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error creating folder:', error);
    throw error;
  }
}

/**
 * 폴더 목록 조회
 */
export async function getFolders(userId) {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('name')
    );

    const querySnapshot = await getDocs(q);
    const folders = [];

    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      const decryptedName = await decryptData(data.name, userId);

      folders.push({
        id: doc.id,
        ...data,
        name: decryptedName,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate()
      });
    }

    return folders;
  } catch (error) {
    console.error('Error getting folders:', error);
    throw error;
  }
}

/**
 * 폴더 수정
 */
export async function updateFolder(userId, folderId, updates) {
  try {
    const folderRef = doc(db, COLLECTION_NAME, folderId);
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    };

    // 이름이 변경되었다면 암호화
    if (updates.name) {
      updateData.name = await encryptData(updates.name, userId);
    }

    await updateDoc(folderRef, updateData);

    return {
      id: folderId,
      ...updates
    };
  } catch (error) {
    console.error('Error updating folder:', error);
    throw error;
  }
}

/**
 * 폴더 삭제
 */
export async function deleteFolder(userId, folderId, moveNotesToFolder = SYSTEM_FOLDERS.ALL) {
  try {
    const batch = writeBatch(db);

    // 1. 폴더 삭제
    const folderRef = doc(db, COLLECTION_NAME, folderId);
    batch.delete(folderRef);

    // 2. 하위 폴더들을 상위 폴더로 이동
    const subFoldersQuery = query(
      collection(db, COLLECTION_NAME),
      where('parentId', '==', folderId)
    );
    const subFolders = await getDocs(subFoldersQuery);

    subFolders.forEach(subFolder => {
      batch.update(doc(db, COLLECTION_NAME, subFolder.id), {
        parentId: null,
        updatedAt: serverTimestamp()
      });
    });

    // 3. 폴더 내 메모들을 지정된 폴더로 이동
    const notesQuery = query(
      collection(db, 'secureNotes'),
      where('folderId', '==', folderId)
    );
    const notes = await getDocs(notesQuery);

    notes.forEach(note => {
      batch.update(doc(db, 'secureNotes', note.id), {
        folderId: moveNotesToFolder,
        updatedAt: serverTimestamp()
      });
    });

    await batch.commit();

    return true;
  } catch (error) {
    console.error('Error deleting folder:', error);
    throw error;
  }
}

/**
 * 폴더별 메모 개수 업데이트
 */
export async function updateFolderNoteCount(userId, folderId) {
  try {
    const notesQuery = query(
      collection(db, 'secureNotes'),
      where('userId', '==', userId),
      where('folderId', '==', folderId)
    );

    const notesSnapshot = await getDocs(notesQuery);
    const noteCount = notesSnapshot.size;

    const folderRef = doc(db, COLLECTION_NAME, folderId);
    await updateDoc(folderRef, {
      noteCount,
      updatedAt: serverTimestamp()
    });

    return noteCount;
  } catch (error) {
    console.error('Error updating folder note count:', error);
    throw error;
  }
}

/**
 * 폴더 트리 구조 생성
 */
export function buildFolderTree(folders) {
  const folderMap = new Map();
  const rootFolders = [];

  // 시스템 폴더 추가
  Object.entries(SYSTEM_FOLDER_INFO).forEach(([id, info]) => {
    const systemFolder = {
      id,
      ...info,
      children: [],
      noteCount: 0
    };
    folderMap.set(id, systemFolder);
    rootFolders.push(systemFolder);
  });

  // 모든 폴더를 맵에 추가
  folders.forEach(folder => {
    folderMap.set(folder.id, { ...folder, children: [] });
  });

  // 트리 구조 생성
  folders.forEach(folder => {
    if (folder.parentId) {
      const parent = folderMap.get(folder.parentId);
      if (parent) {
        parent.children.push(folderMap.get(folder.id));
      }
    } else {
      rootFolders.push(folderMap.get(folder.id));
    }
  });

  return rootFolders;
}

/**
 * 메모를 폴더로 이동
 */
export async function moveNoteToFolder(noteId, folderId) {
  try {
    const noteRef = doc(db, 'secureNotes', noteId);
    await updateDoc(noteRef, {
      folderId,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error moving note to folder:', error);
    throw error;
  }
}

/**
 * 여러 메모를 폴더로 이동
 */
export async function moveNotesToFolder(noteIds, folderId) {
  try {
    const batch = writeBatch(db);

    noteIds.forEach(noteId => {
      const noteRef = doc(db, 'secureNotes', noteId);
      batch.update(noteRef, {
        folderId,
        updatedAt: serverTimestamp()
      });
    });

    await batch.commit();

    return true;
  } catch (error) {
    console.error('Error moving notes to folder:', error);
    throw error;
  }
}